import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UsersIcon,
  UserPlusIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ComputerDesktopIcon,
  BanknotesIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { employeeService } from '../../services/employeeService';
import { getProfilePhotoUrl } from '../../utils/profilePhoto';

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon },
  { name: 'My Profile', path: '/profile', icon: UserIcon },
  { name: 'Attendance', path: '/attendance', icon: CalendarIcon },
  { name: 'Attendance Overview', path: '/attendance-overview', icon: TableCellsIcon, adminOnly: true },
  { name: 'Leave', path: '/leave', icon: ClockIcon },
  { name: 'Payroll', path: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Employees', path: '/employees', icon: UsersIcon, adminOnly: true },
  { name: 'Pending Registrations', path: '/pending-registrations', icon: UserPlusIcon, adminOnly: true },
  { name: 'Asset Management', path: '/assets', icon: ComputerDesktopIcon, adminOnly: true },
  { name: 'Reimbursement', path: '/reimbursement', icon: BanknotesIcon },
  { name: 'Reports', path: '/reports', icon: DocumentTextIcon, adminOnly: true },
  { name: 'Settings', path: '/settings', icon: Cog6ToothIcon },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isHR, isAdminHead, user } = useAuth();
  const location = useLocation();
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadProfilePhoto = async () => {
      if (!user?.employeeId) return;
      try {
        const profile = await employeeService.getMyProfile();
        setProfilePhotoUrl(getProfilePhotoUrl(profile.photoPath) ?? null);
      } catch {
        setProfilePhotoUrl(null);
      }
    };
    loadProfilePhoto();
  }, [user?.employeeId]);

  const filteredNavigation = navigation.filter(
    (item) => {
      if (!item.adminOnly) return true;
      // HR/Admin/Admin Head get full admin access
      if (isHR || isAdminHead) return true;
      return false;
    }
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 w-64 bg-gray-900 text-white z-50
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo with close button */}
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 border-b border-gray-800 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center p-1">
              <img
                src="/logo.png"
                alt="South Asia Consultancy"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-base sm:text-lg font-semibold truncate">South Asia Consultancy</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg touch-manipulation"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 overflow-y-auto" style={{ height: 'calc(100vh - 8rem)' }}>
          <ul className="space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    className={`
                      flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-lg
                      transition-colors duration-200 touch-manipulation
                      ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }
                    `}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info at bottom - profile picture or initials */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-primary-600">
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-semibold">
                  {user?.employee?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.employee ? `${user.employee.firstName} ${user.employee.lastName}` : user?.email}
              </p>
              <p className="text-xs text-gray-400 capitalize">{user?.role?.toLowerCase()}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
